// src/services/demoStatsService.js
//
// Reads for the "RegX progress" section of /statistics. Only numbers come back:
// getCountFromServer returns a count, so no patient record reaches the browser.
// The one exception is the Sheet total, which reads through the same call the
// Coordinator page uses and keeps nothing but how many rows there are.
//
// Each number loads on its own. One that fails comes back null, so its tile or
// chart is left out and the rest of the section still shows.

import {
  collection,
  getCountFromServer,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getCollectionPath,
  getConsentsPath,
  getReferralSubmissionsPath,
} from "./firestorePaths";
import { activeSiteUsesLegacySheet } from "./activeTenant";
import { fetchSheetPatients } from "./sheetPatientsService";
import { isPotentialDuplicateStatus } from "../constants/patientBoxes";
import { addMonths, finishedWeeks, monthOfDate, monthsBetween, monthStart } from "./demoStats";

// RegX's first patient records and first PFT uploads. Anything earlier doesn't
// exist; empty months at a site that started later are trimmed by the charts.
const FIRST_MONTH = "2025-08";
const FIRST_PFT_WEEK = new Date(2026, 9, 5);

const countOf = async (q) => (await getCountFromServer(q)).data().count;

const countBetween = (path, field, start, end) =>
  countOf(
    query(
      collection(db, path),
      where(field, ">=", Timestamp.fromDate(start)),
      where(field, "<", Timestamp.fromDate(end))
    )
  );

/** { "2026-10": count } for every month from FIRST_MONTH through this one. */
const countByMonth = async (path, field, now) => {
  const months = monthsBetween(FIRST_MONTH, monthOfDate(now));
  const counts = await Promise.all(
    months.map((m) => countBetween(path, field, monthStart(m), monthStart(addMonths(m, 1))))
  );
  return Object.fromEntries(months.map((m, i) => [m, counts[i]]));
};

/** The same total as the Coordinator page: consents listed there + the Sheet. */
const loadPatientTotal = async () => {
  const consents = await countOf(query(collection(db, getConsentsPath()), orderBy("signedAt")));
  if (!activeSiteUsesLegacySheet()) return { total: consents, hasSheet: false };
  const rows = await fetchSheetPatients();
  const sheet = rows.filter((p) => !isPotentialDuplicateStatus(p?.status)).length;
  return { total: consents + sheet, hasSheet: true };
};

const loadPftNumbers = async (now) => {
  const filesPath = getCollectionPath("pft_files");
  const importsPath = getCollectionPath("pft_imports");
  const weeks = finishedWeeks(FIRST_PFT_WEEK, now);
  const [uploaded, read, tests, perWeek] = await Promise.all([
    countOf(query(collection(db, filesPath))),
    countOf(query(collection(db, filesPath), orderBy("extractedAt"))),
    countOf(query(collection(db, importsPath))),
    Promise.all(
      weeks.map((w) =>
        countBetween(
          filesPath,
          "extractedAt",
          w,
          new Date(w.getFullYear(), w.getMonth(), w.getDate() + 7)
        )
      )
    ),
  ]);
  return {
    uploaded,
    read,
    tests,
    weeks: weeks.map((week, i) => ({ week, value: perWeek[i] })),
  };
};

const orNull = (promise, what) =>
  promise.catch((err) => {
    console.warn(`RegX progress: could not load ${what}:`, err?.code || err?.message || "error");
    return null;
  });

export const loadProgressNumbers = async (now = new Date()) => {
  const [patientTotal, patientsByMonth, submissionsByMonth, submissionsMoved, pft] = await Promise.all([
    orNull(loadPatientTotal(), "patient total"),
    orNull(countByMonth(getConsentsPath(), "signedAt", now), "patients by month"),
    orNull(countByMonth(getReferralSubmissionsPath(), "submittedAt", now), "submissions by month"),
    orNull(
      countOf(query(collection(db, getReferralSubmissionsPath()), where("movedToReferrals", "==", true))),
      "submissions moved"
    ),
    orNull(loadPftNumbers(now), "PFT numbers"),
  ]);
  return { patientTotal, patientsByMonth, submissionsByMonth, submissionsMoved, pft };
};
